import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as api from '@owl-nest/api-client/latest'
import * as env from '@owl-nest/config'
import { t, tn } from '@owl-nest/localize'
import * as models from '@owl-nest/models'

type GoalCardProps = {
  className?: string
  description: string | undefined
  goal: number | '' | undefined
  goalType?: api.GoalType
  isEditMode?: boolean
  isMember?: boolean
  project: models.project.ProjectForOwner
  subscriptionsCount: number
  title: string | undefined
  trackAndRedirect?: () => void
}

export function GoalCard({
  className,
  description,
  goal,
  goalType,
  isEditMode,
  isMember,
  project,
  subscriptionsCount,
  title,
  trackAndRedirect,
}: GoalCardProps): React.ReactElement {
  const hasGoal = goal !== undefined && goal !== ''
  const width = hasGoal ? Math.floor((subscriptionsCount * 100) / goal) : models.project.progress(project)
  const projectGoalType = goalType ?? project.goal_type
  const userConfig = env.useUserEnv()

  const data = React.useMemo(() => getData(), [])

  return (
    <GoalBlock isMember={isMember} className={className}>
      <plume.Tag>
        <plume.glyphs.crowdfunding.EggFull size={18} />
        <plume.styles.copy.S>{t('Next goal')}</plume.styles.copy.S>
      </plume.Tag>

      {isEditMode && (
        <EditButtonWrapper href={`/projects/${project.id}/backoffice/memberships/`}>
          <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
        </EditButtonWrapper>
      )}

      {title && <plume.styles.heading.XXXS>{title}</plume.styles.heading.XXXS>}

      {description ? (
        <plume.Toggle
          maxHeight={70}
          translations={{
            showLess: t('Show less'),
            showMore: t('Show more'),
          }}
        >
          <plume.WysiwygContent dangerouslySetInnerHTML={{ __html: description }} textSize="small" />
        </plume.Toggle>
      ) : isEditMode ? (
        <plume.styles.copy.S>{t('Set a goal')}</plume.styles.copy.S>
      ) : null}

      <plume.ProgressBar data={data} width={width} />

      {!isMember && (
        <plume.RoundButton onClick={trackAndRedirect} fitToParent prefixIcon={<plume.glyphs.stroke.Plus size={18} />}>
          {t('Become a member')}
        </plume.RoundButton> 
      )}
    </GoalBlock>
  )

  function getData() {
    const hasGoal = goal !== undefined && goal !== ''
    const formattedGoal = hasGoal
      ? plume.accounting.localeMoney(goal, {
          precision: 0,
          rates: env.CURRENCIES_RATES,
          currency: project.currency,
          targetCurrency: project.currency,
          lang: userConfig.lang,
        })
      : 'XX'

    const formattedSubscriptionCount = plume.accounting.localeMoney(subscriptionsCount, {
      precision: 0,
      rates: env.CURRENCIES_RATES,
      currency: project.currency,
      targetCurrency: project.currency,
      lang: userConfig.lang,
    })

    const accountinSetting = {
      ...plume.accounting.NUMBER_FORMAT_CONFIG[userConfig.lang],
      precision: 0,
    }

    return !project.show_goal
      ? tn('%(memberCount)s member', '%(memberCount)s members', subscriptionsCount, {
          memberCount: plume.accounting.formatNumber(subscriptionsCount, accountinSetting),
        })
      : projectGoalType === api.GoalType.QUANTITATIVE
        ? !hasGoal
          ? `${plume.accounting.formatNumber(subscriptionsCount, accountinSetting)}\u00a0<span>/\u00a0XX members`
          : `${plume.accounting.formatNumber(subscriptionsCount, accountinSetting)}\u00a0<span>/\u00a0${tn(
              '%(memberCount)s member',
              '%(memberCount)s members',
              goal,
              {
                memberCount: plume.accounting.formatNumber(goal, accountinSetting),
              },
            )}</span>`
        : `${formattedSubscriptionCount}\u00a0<span>/\u00a0${formattedGoal}\u00a0${t('per month')}           `
  }
}

const EditButtonWrapper = styled.a`
  display: contents;
`

const GoalBlock = styled.div<{ isMember?: boolean }>`
  align-items: center;
  background: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  flex-direction: column;
  gap: 20px;
  justify-content: center;
  margin-bottom: ${(props) => (props.isMember ? '16px' : '40px')};
  margin-inline: 16px;
  margin-top: 40px;
  max-width: 548px;
  padding: 30px 16px 24px 16px;
  position: relative;

  @media screen and (min-width: 585px) {
    width: 100%;
    margin-inline: auto;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-top: 48px;
    margin-bottom: ${(props) => (props.isMember ? '16px' : '0px')};
  }

  ${plume.Tag} {
    border-radius: 4px;
    display: flex;
    outline: 3px solid ${plume.COLORS.PRIMARY_GREY_000};
    padding: 2px 8px;
    position: absolute;
    top: -15px;

    &::first-letter {
      text-transform: capitalize;
    }

    ${plume.glyphs.crowdfunding.EggFull} {
      top: 3px;
      margin-right: 8px;

      g {
        fill: ${(props) => props.theme?.colors?.primary ?? plume.COLORS.PRIMARY_BLUE_500};
      }
    }
  }

  ${plume.styles.heading.XXXS} {
    margin-bottom: -12px;
  }

  ${plume.styles.toggle.ToggleButton} {
    margin: 10px auto 0;
  }

  ${plume.EditButton} {
    position: absolute;
    right: 10px;
    top: 10px;
  }

  ${plume.RoundButton} {
    margin-top: 8px;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      margin-top: 4px;
      width: fit-content;
    }
  }

  ${plume.WysiwygContent} {
    p {
      margin: 0 !important;
    }
  }
`
